// "ITR Document Checklist" template — detailed document request sent to
// clients for ITR filing. Same infrastructure as the other WA/email templates:
// filled client-side, sent through the existing WhatsApp/Email modals.

export const ITR_CHECKLIST_TEMPLATE = `Dear {{client_name}},

Greetings from Krishna Uday & Associates!

To proceed with the preparation and filing of your Income Tax Return for FY {{fy}} (AY {{ay}}), kindly share the following details and documents, wherever applicable:

*1. EMPLOYMENT INCOME (SALARY)*
☐ Form 16 issued by all employers during the year
☐ Salary break-up (Basic, Allowances, Perquisites, Bonus/Incentives, ESOPs/RSUs, other benefits)
☐ Details of any change of employment during the year
☐ Details of exempt allowances and reimbursements claimed

*2. BANK ACCOUNTS AND INTEREST INCOME*
☐ Bank name, account number, IFSC, account type (Savings/Current/Deposit/NRE/NRO)
☐ Details of accounts closed during the year, if any
☐ Bank statements for all accounts (Apr–Mar), in Excel format
☐ Interest certificates — Savings, FD, RD, Corporate deposits, Post Office deposits, other interest-bearing investments

*3. DIVIDEND INCOME*
☐ Dividend statements from Shares
☐ Dividend statements from Mutual Funds
☐ Dividend statements from Foreign Investments
☐ Dividend statements from any other investments

*4. HOUSE PROPERTY AND HOUSING LOAN*
☐ Property address with PIN code (for each property owned)
☐ Nature of property — self-occupied / let out / deemed let out
☐ Ownership share, co-owner details, tenant details
☐ Rent received during the year
☐ Municipal taxes paid + payment receipts
☐ Housing loan interest certificate
☐ Bank/institution name, PAN, loan account number, sanction date
☐ Outstanding loan amount as on 31 March {{fy_end_year}}
☐ Details of co-borrowers, if any
☐ Details of property purchased or under construction during the year

*5. INVESTMENTS, SHARES, MUTUAL FUNDS & CRYPTO*
☐ Demat account statements
☐ Trading account statements
☐ Realised Profit & Loss / Tax P&L statement (with purchase/sale dates, values, quantity)
☐ Crypto/VDA — exchange-wise transaction statements
☐ Crypto/VDA — realised gain/loss reports
☐ Crypto/VDA — details of purchases, sales, transfers, holdings

*6. CAPITAL ASSETS SOLD DURING THE YEAR*
☐ Sale deed/agreement, sale consideration, date of sale
☐ Purchase deed/agreement, purchase consideration, date of purchase
☐ Improvement costs (renovation, construction, alterations, additions) with supporting documents
☐ Details of investment made for capital gains exemption (new property, specified bonds, Capital Gains Account Scheme, etc.)

*7. BUSINESS / PROFESSIONAL INCOME (if applicable)*
☐ Trade name & nature of business
☐ Books of accounts, if available
☐ Bank statements used for business/professional activity
☐ Major receipts/expenses not evident from bank statements
☐ GST registration details, if registered
☐ GST returns filed during the year — GSTR-1, GSTR-3B

*8. TAX-SAVING INVESTMENTS & DEDUCTIONS*
☐ 80C — Life Insurance, EPF, PPF, ELSS, Tax-saving FDs, Sukanya Samriddhi, Housing loan principal, Tuition fees
☐ NPS contributions
☐ Health Insurance Premium (Self, Family, Parents)
☐ Interest on Education Loan
☐ Donations eligible for deduction
☐ Interest on Savings Account / Deposits eligible for deduction
☐ Disability-related deductions, if any
☐ Any other deduction under the Income Tax Act

*9. AGRICULTURAL INCOME (if applicable)*
☐ Details of agricultural income earned during the year
☐ Location of agricultural land
☐ Details of produce sold + supporting records

*10. OTHER INCOME*
☐ Interest income not covered above
☐ Family pension
☐ Commission or brokerage
☐ Freelance or consulting income
☐ Gifts received
☐ Lottery, game show, or betting winnings
☐ Any other taxable or exempt income

*11. FOREIGN ASSETS & FOREIGN INCOME (if applicable)*
☐ Foreign bank accounts, shares/ETFs/MFs, retirement accounts, property, business interests, signing authority accounts
☐ Foreign dividends, interest, rental income, capital gains, employment income earned outside India
☐ Taxpayer ID (TIN) or Passport number
☐ Foreign tax paid — statements/certificates, tax residency documents

*12. RESIDENTIAL STATUS / FOREIGN TRAVEL (if applicable)*
☐ Overseas travel details — departure/arrival dates
☐ Details of stay in India during the year
☐ Tax residency in any foreign country/jurisdiction, if applicable

*13. INVESTMENTS IN UNLISTED INDIAN COMPANIES (if applicable)*
☐ Company name, PAN (if available)
☐ Number of shares held, date of acquisition, cost of acquisition
☐ Details of shares purchased or sold during the year

*14. DIRECTORSHIP IN COMPANIES (if applicable)*
☐ Company name, PAN, DIN
☐ Whether the company is listed or unlisted

*15. PARTNERSHIP FIRM / LLP (if applicable)*
☐ Firm/LLP name and PAN
☐ Capital balance as on 31 March {{fy_end_year}}
☐ Capital account statement for the year
☐ Share of profit % and amount received
☐ Interest received on capital/current account
☐ Remuneration/salary received from the firm
☐ Copy of financial statements

*16. ANY OTHER RELEVANT INFORMATION*
☐ Any income, investment, transaction, asset, liability, deduction, exemption, tax payment, foreign holding, or business interest not covered above

📌 *Note:* Additional documents/clarifications may be required after review, or if total income exceeds ₹1 crore (assets & liabilities disclosure) or foreign assets are involved.

Please share these at your earliest convenience so we can proceed smoothly with your filing.

For any queries, feel free to reach out.

Warm regards,
Krishna Uday & Associates
Chartered Accountants
{{firm_phone}} | {{firm_email}}`;

// "AY 2026-27" -> { fy: "2025-26", ay: "2026-27", fyEndYear: "2026" }
export function deriveYearsFromAy(ayString) {
  const m = String(ayString || '').match(/(\d{4})-(\d{2})/);
  if (!m) return { fy: '', ay: String(ayString || '').replace(/^AY\s*/, ''), fyEndYear: '' };
  const ayStart = parseInt(m[1], 10);
  return {
    ay: `${ayStart}-${m[2]}`,
    fy: `${ayStart - 1}-${String(ayStart).slice(-2)}`,
    fyEndYear: String(ayStart),
  };
}

// clientName may be null for bulk sends — the {{client_name}} placeholder is
// left in place and substituted per recipient at send time.
export function fillItrChecklist({ clientName, ay, firmPhone, firmEmail }) {
  const y = deriveYearsFromAy(ay);
  let msg = ITR_CHECKLIST_TEMPLATE
    .replaceAll('{{fy}}', y.fy || '____')
    .replaceAll('{{ay}}', y.ay || '____')
    .replaceAll('{{fy_end_year}}', y.fyEndYear || '____')
    .replaceAll('{{firm_phone}}', firmPhone || '')
    .replaceAll('{{firm_email}}', firmEmail || '');
  // Tidy the signature line if firm contact is missing
  msg = msg.replace(/\n\s*\|\s*$/,'').replace(/\n\s*\|\s*\n/, '\n').replace(/ \| $/m, '');
  if (clientName) msg = msg.replaceAll('{{client_name}}', clientName);
  return msg;
}
